<template>
  <div class="h-full flex flex-col overflow-hidden">

    <!-- Header -->
    <div class="px-2 py-1.5 text-xs uppercase tracking-widest text-gray-500 border-b border-gray-700 shrink-0">
      Meine Repositories
    </div>

    <!-- Repo list -->
    <div class="flex-1 overflow-y-auto">

      <div v-if="repoStore.userRepos.length === 0" class="px-4 py-6 text-xs text-gray-500 text-center">
        Noch keine Repositories vorhanden.
      </div>

      <div
        v-for="repo in repoStore.userRepos"
        :key="repo.id"
        class="border-b border-gray-700/50"
      >
        <!-- Repo-Kopf -->
        <div class="px-3 py-2.5">

          <!-- Label + Status -->
          <div class="flex items-center justify-between gap-2 mb-1.5">
            <span class="text-sm text-gray-100 truncate font-medium">{{ repo.label }}</span>
            <span class="text-xs shrink-0 font-mono" :class="statusColor(repo.cloneStatus)">
              {{ statusLabel(repo.cloneStatus) }}
            </span>
          </div>

          <!-- NAS URL -->
          <div class="text-xs text-gray-500 truncate mb-2" :title="repo.nasUrl">{{ repo.nasUrl }}</div>

          <!-- Error hint -->
          <div v-if="repo.cloneStatus === 'error'" class="text-xs text-red-400 mb-2">
            Clone fehlgeschlagen. Bitte Zugangsdaten und Server-Verbindung prüfen, dann löschen und neu anlegen.
          </div>

          <!-- Actions -->
          <div class="flex gap-1.5">
            <button
              class="px-2 py-0.5 text-xs rounded bg-blue-600 hover:bg-blue-500 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              :disabled="repo.cloneStatus !== 'ready'"
              @click="openRepo(repo)"
            >Öffnen</button>

            <button
              class="px-2 py-0.5 text-xs rounded bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              :disabled="repo.cloneStatus !== 'ready' || pulling === repo.id"
              @click="pullRepo(repo)"
            >{{ pulling === repo.id ? '…' : 'Pull' }}</button>

            <button
              class="px-2 py-0.5 text-xs rounded bg-gray-700 hover:bg-red-800 text-gray-300 hover:text-red-200 transition-colors ml-auto"
              @click="deleteRepo(repo)"
            >Löschen</button>
          </div>
        </div>

        <!-- Datenbank-Sektion (nur wenn ready) -->
        <div v-if="repo.cloneStatus === 'ready'" class="border-t border-gray-700/50 bg-gray-900/40">

          <!-- Toggle -->
          <button
            class="w-full flex items-center justify-between px-3 py-2 text-xs text-gray-400 hover:text-gray-200 transition-colors"
            @click="toggleDb(repo.id)"
          >
            <span class="flex items-center gap-1.5">
              <span>Datenbank</span>
              <span v-if="repo.dbStatus" class="font-mono" :class="dbStatusColor(repo.dbStatus)">
                {{ dbStatusLabel(repo.dbStatus) }}
              </span>
            </span>
            <span class="text-gray-600">{{ expandedDb === repo.id ? '▲' : '▼' }}</span>
          </button>

          <!-- Expanded: Credentials + Import -->
          <div v-if="expandedDb === repo.id" class="px-3 pb-3">

            <!-- Loading Credentials -->
            <div v-if="loadingDb === repo.id" class="text-xs text-gray-500 py-2">
              Lade Zugangsdaten…
            </div>

            <!-- DB Error -->
            <div v-else-if="repo.dbStatus === 'error'" class="py-2">
              <p class="text-xs text-red-400 mb-2">
                Datenbank konnte nicht angelegt werden. Bitte prüfe die Servereinstellungen (IDE_DB_PROVISIONER_USER/PASS).
              </p>
              <button
                class="px-2 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                :disabled="retryingDb === repo.id"
                @click="retryDb(repo.id)"
              >{{ retryingDb === repo.id ? '…' : 'Erneut versuchen' }}</button>
              <div v-if="retryDbError[repo.id]" class="mt-2 text-xs text-red-400 font-mono break-all">
                {{ retryDbError[repo.id] }}
              </div>
            </div>

            <!-- DB Provisioning -->
            <div v-else-if="repo.dbStatus === 'provisioning'" class="text-xs text-yellow-400 py-2">
              Datenbank wird angelegt…
            </div>

            <!-- Credentials -->
            <div v-else-if="dbCredentials[repo.id]" class="space-y-1 mt-1">

              <div class="grid grid-cols-[80px_1fr_auto] items-center gap-x-2 gap-y-1">
                <!-- Host -->
                <span class="text-gray-500 text-xs">Host</span>
                <span class="font-mono text-xs text-gray-200 truncate">{{ dbCredentials[repo.id].dbHost }}</span>
                <button class="text-gray-600 hover:text-blue-400 text-xs" @click="copy(dbCredentials[repo.id].dbHost)">⎘</button>
                <!-- Port -->
                <span class="text-gray-500 text-xs">Port</span>
                <span class="font-mono text-xs text-gray-200">{{ dbCredentials[repo.id].dbPort }}</span>
                <button class="text-gray-600 hover:text-blue-400 text-xs" @click="copy(String(dbCredentials[repo.id].dbPort))">⎘</button>
                <!-- Datenbank -->
                <span class="text-gray-500 text-xs">Datenbank</span>
                <span class="font-mono text-xs text-gray-200 truncate">{{ dbCredentials[repo.id].dbName }}</span>
                <button class="text-gray-600 hover:text-blue-400 text-xs" @click="copy(dbCredentials[repo.id].dbName)">⎘</button>
                <!-- Benutzer -->
                <span class="text-gray-500 text-xs">Benutzer</span>
                <span class="font-mono text-xs text-gray-200 truncate">{{ dbCredentials[repo.id].dbUsername }}</span>
                <button class="text-gray-600 hover:text-blue-400 text-xs" @click="copy(dbCredentials[repo.id].dbUsername)">⎘</button>
                <!-- Passwort -->
                <span class="text-gray-500 text-xs">Passwort</span>
                <span class="font-mono text-xs text-gray-200 truncate">
                  {{ showPassword[repo.id] ? dbCredentials[repo.id].dbPassword : '••••••••••••' }}
                </span>
                <div class="flex gap-1">
                  <button class="text-gray-600 hover:text-blue-400 text-xs" @click="togglePassword(repo.id)">
                    {{ showPassword[repo.id] ? '🙈' : '👁' }}
                  </button>
                  <button class="text-gray-600 hover:text-blue-400 text-xs" @click="copy(dbCredentials[repo.id].dbPassword)">⎘</button>
                </div>
              </div>

              <!-- .env kopieren -->
              <button
                class="mt-2 w-full px-2 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
                @click="copyEnvFormat(repo.id)"
              >.env-Format kopieren</button>

              <!-- Letzter Import -->
              <div v-if="repo.dbLastImportAt" class="text-xs text-gray-600 mt-1">
                Letzter Import: {{ formatDate(repo.dbLastImportAt) }}
              </div>

              <!-- SQL-Import -->
              <div class="mt-3 border-t border-gray-700/50 pt-3">
                <div class="text-xs text-gray-500 mb-1.5">SQL importieren</div>

                <div class="flex gap-1.5 items-center">
                  <label class="flex-1 px-2 py-1 text-xs rounded bg-gray-800 border border-gray-600 text-gray-400 cursor-pointer hover:border-blue-500 transition-colors truncate">
                    {{ importFiles[repo.id]?.name ?? 'Datei wählen…' }}
                    <input
                      type="file"
                      accept=".sql"
                      class="hidden"
                      @change="onFileSelect(repo.id, $event)"
                    />
                  </label>
                  <button
                    class="px-2 py-1 text-xs rounded bg-blue-600 hover:bg-blue-500 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                    :disabled="!importFiles[repo.id] || repoStore.importingRepoId === repo.id"
                    @click="startImport(repo.id)"
                  >
                    {{ repoStore.importingRepoId === repo.id ? '…' : 'Importieren' }}
                  </button>
                </div>

                <div v-if="repoStore.importErrors[repo.id]" class="text-xs text-red-400 mt-1">
                  {{ repoStore.importErrors[repo.id] }}
                </div>
                <div v-if="importSuccess[repo.id]" class="text-xs text-green-400 mt-1">
                  Import erfolgreich abgeschlossen.
                </div>
              </div>
            </div>

            <!-- Fehler beim Laden -->
            <div v-else-if="dbLoadError[repo.id]" class="text-xs text-red-400 py-2">
              {{ dbLoadError[repo.id] }}
            </div>

          </div>
        </div>

      </div>
    </div>

    <!-- Add form -->
    <div class="border-t border-gray-700 px-3 py-3 shrink-0">
      <div class="text-xs text-gray-500 uppercase tracking-wider mb-2">Repository hinzufügen</div>

      <div class="flex flex-col gap-1.5">
        <input
          v-model="form.label"
          class="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
          placeholder="Anzeigename (z.B. Kundenprojekt Alpha)"
        />
        <input
          v-model="form.nasUrl"
          class="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
          placeholder="https://nas.domain.com/repo.git oder ssh://user@nas/repo.git"
        />
        <input
          v-model="form.username"
          class="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
          placeholder="Git Username"
        />
        <input
          v-model="form.password"
          type="password"
          class="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
          placeholder="Git Passwort"
        />

        <p v-if="formError" class="text-xs text-red-400">{{ formError }}</p>

        <button
          class="w-full mt-0.5 px-2 py-1 text-xs rounded bg-blue-600 hover:bg-blue-500 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="adding"
          @click="addRepo"
        >{{ adding ? 'Wird hinzugefügt…' : 'Repository hinzufügen' }}</button>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRepoStore, type UserRepository, type RepoDatabase } from './repoStore'
import { extractMessage } from '@/services/apiClient'
import { onDbImportComplete } from '@/websocket/wsClient'

const emit = defineEmits<{ (e: 'open-explorer'): void }>()

const repoStore = useRepoStore()

const form      = ref({ label: '', nasUrl: '', username: '', password: '' })
const adding    = ref(false)
const pulling   = ref<number | null>(null)
const formError = ref('')

// DB Panel State
const expandedDb    = ref<number | null>(null)
const loadingDb     = ref<number | null>(null)
const retryingDb    = ref<number | null>(null)
const retryDbError  = ref<Record<number, string>>({})
const dbCredentials = ref<Record<number, RepoDatabase>>({})
const dbLoadError   = ref<Record<number, string>>({})
const showPassword  = ref<Record<number, boolean>>({})

// Import State
const importFiles   = ref<Record<number, File>>({})
const importSuccess = ref<Record<number, boolean>>({})

let offRepoStatus:    (() => void) | null = null
let offDbImport:      (() => void) | null = null
let offDbComplete:    (() => void) | null = null

onMounted(async () => {
  await repoStore.fetchUserRepos()
  offRepoStatus = repoStore.initRepoStatusListener()
  offDbImport   = repoStore.initDbImportListener()

  // Lokal auf db:import-complete hören um importSuccess-Flag zu setzen
  offDbComplete = onDbImportComplete(({ repoId }) => {
    importSuccess.value = { ...importSuccess.value, [repoId]: true }
    delete importFiles.value[repoId]
    // Credentials neu laden damit lastImportAt aktuell ist
    delete dbCredentials.value[repoId]
  })
})

onUnmounted(() => {
  offRepoStatus?.()
  offDbImport?.()
  offDbComplete?.()
})

async function addRepo(): Promise<void> {
  formError.value = ''
  if (!form.value.label || !form.value.nasUrl || !form.value.username || !form.value.password) {
    formError.value = 'Alle Felder sind erforderlich.'
    return
  }
  adding.value = true
  try {
    await repoStore.createRepo(form.value)
    form.value = { label: '', nasUrl: '', username: '', password: '' }
  } catch (err) {
    formError.value = err instanceof Error ? err.message : String(err)
  } finally {
    adding.value = false
  }
}

async function deleteRepo(repo: UserRepository): Promise<void> {
  if (!confirm(`"${repo.label}" wirklich löschen?\n\nDie zugehörige Datenbank und alle lokalen Dateien werden ebenfalls gelöscht.`)) return
  await repoStore.deleteRepo(repo.id)
  // Panel-State aufräumen
  delete dbCredentials.value[repo.id]
  delete importFiles.value[repo.id]
  if (expandedDb.value === repo.id) expandedDb.value = null
}

async function pullRepo(repo: UserRepository): Promise<void> {
  pulling.value = repo.id
  try {
    await repoStore.pullRepo(repo.id)
  } finally {
    pulling.value = null
  }
}

function openRepo(repo: UserRepository): void {
  repoStore.activateRepo(repo)
  emit('open-explorer')
}

// ── DB Panel ─────────────────────────────────────────────────────────────────

async function retryDb(repoId: number): Promise<void> {
  retryingDb.value = repoId
  delete retryDbError.value[repoId]
  try {
    await repoStore.retryDatabase(repoId)
    const repo = repoStore.userRepos.find(r => r.id === repoId)
    if (repo?.dbStatus === 'ready' && !dbCredentials.value[repoId]) {
      loadingDb.value = repoId
      try {
        dbCredentials.value = { ...dbCredentials.value, [repoId]: await repoStore.fetchDatabase(repoId) }
      } catch { /* shown via dbLoadError */ }
      finally { loadingDb.value = null }
    }
  } catch (err) {
    retryDbError.value = { ...retryDbError.value, [repoId]: extractMessage(err) }
  } finally {
    retryingDb.value = null
  }
}

async function toggleDb(repoId: number): Promise<void> {
  if (expandedDb.value === repoId) {
    expandedDb.value = null
    return
  }
  expandedDb.value = repoId

  // Credentials nur laden wenn noch nicht geladen und DB bereit ist
  const repo = repoStore.userRepos.find(r => r.id === repoId)
  if (!dbCredentials.value[repoId] && repo?.dbStatus === 'ready') {
    loadingDb.value = repoId
    delete dbLoadError.value[repoId]
    try {
      dbCredentials.value = { ...dbCredentials.value, [repoId]: await repoStore.fetchDatabase(repoId) }
    } catch (err) {
      dbLoadError.value = { ...dbLoadError.value, [repoId]: extractMessage(err) }
    } finally {
      loadingDb.value = null
    }
  }
}

function togglePassword(repoId: number): void {
  showPassword.value = { ...showPassword.value, [repoId]: !showPassword.value[repoId] }
}

function copy(value: string): void {
  navigator.clipboard.writeText(value).catch(() => {})
}

function copyEnvFormat(repoId: number): void {
  const c = dbCredentials.value[repoId]
  if (!c) return
  copy([
    `DB_HOST=${c.dbHost}`,
    `DB_PORT=${c.dbPort}`,
    `DB_DATABASE=${c.dbName}`,
    `DB_USERNAME=${c.dbUsername}`,
    `DB_PASSWORD=${c.dbPassword}`,
  ].join('\n'))
}

// ── SQL Import ────────────────────────────────────────────────────────────────

function onFileSelect(repoId: number, event: Event): void {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (file) {
    importFiles.value  = { ...importFiles.value, [repoId]: file }
    importSuccess.value = { ...importSuccess.value, [repoId]: false }
    delete repoStore.importErrors[repoId]
  }
}

async function startImport(repoId: number): Promise<void> {
  const file = importFiles.value[repoId]
  if (!file) return

  importSuccess.value = { ...importSuccess.value, [repoId]: false }

  try {
    await repoStore.importSql(repoId, file)
    // Erfolg kommt via WebSocket — importSuccess wird nach db:import-complete gesetzt
  } catch (err) {
    repoStore.importErrors[repoId] = extractMessage(err)
    repoStore.importingRepoId = null
  }
}

// ── Formatierung ──────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

const STATUS_COLOR: Record<UserRepository['cloneStatus'], string> = {
  ready:   'text-green-400',
  cloning: 'text-yellow-400',
  error:   'text-red-400',
  pending: 'text-gray-500',
}

const STATUS_LABEL: Record<UserRepository['cloneStatus'], string> = {
  ready:   '✓ Bereit',
  cloning: '⟳ Wird geklont…',
  error:   '✗ Fehler',
  pending: '○ Ausstehend',
}

function statusColor(status: UserRepository['cloneStatus']): string {
  return STATUS_COLOR[status]
}

function statusLabel(status: UserRepository['cloneStatus']): string {
  return STATUS_LABEL[status]
}

function dbStatusColor(status: string): string {
  return status === 'ready' ? 'text-green-400' : status === 'error' ? 'text-red-400' : 'text-yellow-400'
}

function dbStatusLabel(status: string): string {
  return status === 'ready' ? '● DB' : status === 'error' ? '✗ DB-Fehler' : '⟳ DB…'
}
</script>
